"use client";

import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const SolutionsSlider = () => {
  const sliderRef = useRef(null);
  const containerRef = useRef(null); // For Intersection Observer
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const solutionItems = [
    {
      id: 1,
      title: "Transforming the Way Ethiopia Transacts",
      description:
        "Arifpay's Next-Generation Payment platform reshapes how businesses and consumers engage, offering versatile, locally-tailored solutions that meet the evolving demands of a dynamic market.",
      icon: "💳",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #9333ea 100%)"
    },
    {
      id: 2,
      title: "Elevating Business Operations",
      description:
        "Arifpay is transforming operational efficiency across estate management, security, merchant digitization, and regulatory compliance through smart, cutting-edge technology built for modern enterprises.",
      icon: "🚀",
      gradient: "linear-gradient(135deg, #10b981 0%, #0d9488 100%)"
    },
    {
      id: 3,
      title: "Empowering Business Operations",
      description:
        "Arifpay delivers a robust suite of business tools - including cloud-based and on-terminal APIs-through its integrated Payment Platform as a Service (IPPaaS), enabling seamless innovation and growth.",
      icon: "⚡",
      gradient: "linear-gradient(135deg, #f97316 0%, #dc2626 100%)"
    },
  ];

  // Intersection Observer for continuous animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          } else {
            entry.target.classList.remove("visible"); // Re-animates on scroll back
          }
        });
      },
      { threshold: 0.3, rootMargin: "0px 0px -100px 0px" }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, []);

  // All your existing swipe/page logic remains unchanged
  const handleTouchStart = (e) => setTouchStart(e.targetTouches[0].clientX);
  const handleTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    const totalPages = Math.ceil(solutionItems.length / itemsPerPage);

    if (isLeftSwipe && currentPage < totalPages) handlePageChange(currentPage + 1);
    if (isRightSwipe && currentPage > 1) handlePageChange(currentPage - 1);
  };

  const handlePageChange = (pageNumber) => {
    if (sliderRef.current && itemsPerPage > 0) {
      const containerWidth = sliderRef.current.offsetWidth;
      let scrollDistance = containerWidth * (pageNumber - 1);
      sliderRef.current.scrollTo({ left: scrollDistance, behavior: "smooth" });
      setCurrentPage(pageNumber);
    }
  };

  const navigateNext = () => {
    const totalPages = Math.ceil(solutionItems.length / itemsPerPage);
    if (currentPage < totalPages) handlePageChange(currentPage + 1);
  };

  const navigatePrev = () => {
    if (currentPage > 1) handlePageChange(currentPage - 1);
  };

  useEffect(() => {
    const updateItemsPerPage = () => {
      if (window.innerWidth < 640) setItemsPerPage(1);
      else if (window.innerWidth < 1024) setItemsPerPage(2);
      else setItemsPerPage(3);
    };
    updateItemsPerPage();
    window.addEventListener("resize", updateItemsPerPage);
    return () => window.removeEventListener("resize", updateItemsPerPage);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    if (sliderRef.current) sliderRef.current.scrollTo({ left: 0, behavior: "smooth" });
  }, [itemsPerPage]);

  useEffect(() => {
    const container = sliderRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const containerWidth = container.offsetWidth;
      const calculatedPage = Math.round(scrollLeft / containerWidth) + 1;
      if (calculatedPage !== currentPage && calculatedPage <= Math.ceil(solutionItems.length / itemsPerPage)) {
        setCurrentPage(calculatedPage);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [itemsPerPage, currentPage, solutionItems.length]);

  const totalPages = Math.ceil(solutionItems.length / itemsPerPage);

  // Styles (same as before, with minor tweaks)
  const containerStyle = {
    position: 'relative',
    width: '100%',
    maxWidth: '1280px',
    margin: '0 auto',
    padding: window.innerWidth < 768 ? '0' : '64px 16px',
    background: 'white',
  };

  const headerStyle = { textAlign: 'center' };
  const subtitleStyle = { color: '#16a34a', fontWeight: '600', fontSize: '28px', letterSpacing: '0.05em', marginBottom: '8px' };
  const titleStyle = { fontSize: window.innerWidth < 768 ? '30px' : '36px', fontWeight: 'bold', color: '#111827', marginBottom: '16px' };
  const dividerStyle = { width: '96px', height: '4px', background: 'linear-gradient(90deg, #10b981 0%, #3b82f6 100%)', margin: '0 auto 48px', borderRadius: '9999px' };

  const cardsContainerStyle = {
    display: 'flex',
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollBehavior: 'smooth',
    scrollSnapType: 'x mandatory',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  };

  const cardWrapperStyle = {
    flexShrink: 0,
    width: itemsPerPage === 1 ? '100%' : itemsPerPage === 2 ? '50%' : '33.333%',
    padding: itemsPerPage === 1 ? '0 16px' : '0 12px',
    scrollSnapAlign: 'start',
    boxSizing: 'border-box',
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <p style={subtitleStyle}>OUR SOLUTIONS</p>
        <h2 style={titleStyle}>Innovative Solutions For Seamless Transactions</h2>
        <div style={dividerStyle}></div>
      </div>

      {/* Main animated container */}
      <div ref={containerRef} style={{ overflow: "hidden", padding: "0 20px", boxSizing: "border-box" }}>
        <div
          ref={sliderRef}
          style={cardsContainerStyle}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {solutionItems.map((solution, index) => {
            const [isHovered, setIsHovered] = useState(false);

            return (
              <div key={solution.id} style={cardWrapperStyle} className="solution-card">
                <div
                  style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    boxShadow: isHovered ? '0 25px 50px -12px rgba(0, 0, 0, 0.25)' : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.5s ease',
                    transform: isHovered ? 'translateY(-8px)' : 'translateY(0)',
                    height: '100%',
                    border: '1px solid #f3f4f6',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  <div style={{ height: '8px', background: solution.gradient, borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}></div>
                  <div style={{ padding: '32px' }}>
                    <div
                      style={{
                        width: '64px',
                        height: '64px',
                        background: solution.gradient,
                        borderRadius: '16px',
                        display: window.innerWidth < 768 ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '24px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        marginBottom: '24px',
                        transition: 'transform 0.3s ease',
                        transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                      }}
                    >
                      {solution.icon}
                    </div>
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: isHovered ? '#374151' : '#111827', marginBottom: '16px', transition: 'color 0.3s ease' }}>
                      {solution.title}
                    </h3>
                    <p style={{ color: isHovered ? '#374151' : '#6b7280', lineHeight: '1.625', fontSize: '18px', transition: 'color 0.3s ease' }}>
                      {solution.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Continuous "One by One from Right" Animation */}
        <style jsx>{`
          .solution-card {
            opacity: 0;
            transform: translateX(100px);
            transition: opacity 0.9s ease-out, transform 0.9s ease-out;
          }

          /* Staggered delays */
          .visible .solution-card:nth-child(1) { transition-delay: 0.2s; }
          .visible .solution-card:nth-child(2) { transition-delay: 0.4s; }
          .visible .solution-card:nth-child(3) { transition-delay: 0.6s; }

          /* Final visible state */
          .visible .solution-card {
            opacity: 1 !important;
            transform: translateX(0) !important;
          }

          /* Hide scrollbar */
          div[style*="overflowX: auto"]::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '40px', gap: '12px' }}>
          {Array.from({ length: totalPages }).map((_, index) => (
            <button
              key={index}
              onClick={() => handlePageChange(index + 1)}
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: currentPage === index + 1 ? '#16a34a' : '#d1d5db',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: currentPage === index + 1 ? 'scale(1.3)' : 'scale(1)',
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SolutionsSlider;